/**
 * 言語別の生成状況 (`GET /language-health`) の表示ロジック (SBI-17)。
 *
 * 画面 (`LanguageHealthScreen`) から切り出した純関数。DOM に依存しないので
 * 単体テストで「どの言語を欠落として扱うか」「並び順」を固定できる。
 */
import type { LanguageHealthStats } from '../api/languageHealth';
import { BASE_LANGUAGE_KEYS, findPollyLanguage } from '../data/pollyVoices';

/** 1 行ぶん (言語キーと集計値)。 */
export type LanguageHealthRow = readonly [languageKey: string, stats: LanguageHealthStats];

/**
 * 言語キー → 表示名 (`日本語 (ja)`)。
 *
 * マスタに無いキー (任意放送で追加された言語など) はキーのみを返す。
 */
export function languageDisplayName(languageKey: string): string {
  const option = findPollyLanguage(languageKey);
  return option ? `${option.label} (${languageKey})` : languageKey;
}

/**
 * 表示行を並べ替える。基本 4 言語 (日 → 英 → 中 → 韓) を先頭に、それ以外は
 * 言語キーの昇順。放送順 (日 → 英 → 中 → 韓) と画面の並びを一致させる。
 */
export function sortLanguageRows(
  byLanguage: Record<string, LanguageHealthStats>,
): LanguageHealthRow[] {
  const rank = (key: string): number => {
    const index = BASE_LANGUAGE_KEYS.indexOf(key);
    return index === -1 ? BASE_LANGUAGE_KEYS.length : index;
  };
  return Object.entries(byLanguage).sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
}

/**
 * 音声が生成されていない言語 (`missing_audio_key_count > 0`) の行だけを返す。
 *
 * 欠落の判定を画面とテストで共有するため関数にしている。`empty_message_count`
 * (文面欠落) は音声欠落とは別の事象なので、ここには含めない。
 */
export function lossyRows(rows: readonly LanguageHealthRow[]): LanguageHealthRow[] {
  return rows.filter(([, stats]) => stats.missing_audio_key_count > 0);
}
